import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy} from '@angular/core';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {OptionTicker, parseOptionTicker} from "../../options-common/options.model";
import {LastQuoteCacheService} from "../../last-quote-cache.service";
import {MessageBusService} from "../../message-bus.service";
import {QuoteDto} from "../../shell-communication/dtos/quote-dto.class";
import {DetectMethodChanges, getStrikeStep, isValidNumber, isVoid} from "../../utils";
import {UserSettingsService} from "../../user-settings.service";
import {ToastrService} from "ngx-toastr";
import {AtmStrikeService} from "../../common-services/atm-strike-service/atm-strike.service";
import {AtmStrikeChangedEvent} from "../../common-services/atm-strike-service/atm-strike-changed-event";
import {SessionService} from "../../authentication/session-service.service";
import {TradingInstrumentDisplayNameService} from "../../trading-instruments/trading-instrument-display-name.service";

export interface TransactionLeg {
    ticker: string;
    qty: number;
    priceAsOwned: boolean;
}

interface ParsedLeg {
    original: OptionTicker;
    qty: number;
    priceAsOwned: boolean;
}

interface OffsetRow {
    offsetPoints: number;
    offsetLabel: string;
    strike: number;           // <-- add this
    tickers: string[];
    netPrice: number | null;
}

export interface PricingStepsSettings {
    numOfSteps: number;
    stepSize: number;
}

export interface GroupMetadata {
    name: string;
    color: string;
    expiration: string;
    legType: string;
}

@Component({
    selector: 'ets-pricing-steps-popup',
    templateUrl: './pricing-steps-popup.component.html',
    styleUrls: ['./pricing-steps-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PricingStepsPopupComponent implements OnDestroy {

    isVisible = false;
    offsetRows: OffsetRow[] = [];
    settings: { numOfSteps?: number, stepSize?: number } = {};
    originalLegs: TransactionLeg[] = [];
    isLoading = false;
    metadata: GroupMetadata;

    private parsedLegs: ParsedLeg[] = [];
    private tickerToRow: Record<string, OffsetRow[]> = {};
    private destroy$ = new Subject<void>();
    private quoteSubscriptionActive = false;
    private hcf = 1;
    private underlying: string;
    underlyingDisplayName: string;
    esHedgingEnabled = false;
    private _subscriptions: Subscription[] = [];

    constructor(
        private _changeDetector: ChangeDetectorRef,
        private lastQuoteCache: LastQuoteCacheService,
        private messageBus: MessageBusService,
        private _userSettings: UserSettingsService,
        private _toastr: ToastrService,
        private _atmStrikeService: AtmStrikeService,
        private _sessionService: SessionService,
        private _displayNameService: TradingInstrumentDisplayNameService,
    ) {
        this.messageBus.of<{ enabled: boolean }>('Hg.EsHedgingModeChanged')
            .pipe(takeUntil(this.destroy$))
            .subscribe(msg => {
                this.esHedgingEnabled = msg.payload.enabled;
                this._changeDetector.detectChanges();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Opens the popup with strike offset analysis for the given transaction legs
     */
    @DetectMethodChanges()
    public show(legs: TransactionLeg[], hcf: number, metadata: GroupMetadata): void {
        if (!legs || legs.length === 0) {
            return;
        }

        this.isLoading = true;

        try {

            this.reset();

            this.metadata = metadata;

            this.originalLegs = legs;

            this.parsedLegs = this.parseLegs(legs);

            if (this.parsedLegs.length === 0) {
                return;
            }

            this.hcf = hcf;

            const underlying = this.parsedLegs[0].original.underlying;

            this.underlying = underlying;

            const isMixed = this.parsedLegs.some(l => l.original.underlying !== underlying);
            this.underlyingDisplayName = isMixed
                ? null
                : (this._displayNameService.getDisplayNameForTicker(underlying) || underlying);

            let settings: PricingStepsSettings =
                this._userSettings.getValue<PricingStepsSettings>(`pricing-steps.${underlying}.settings`);

            if (isVoid(settings)) {
                settings = {numOfSteps: 5, stepSize: getStrikeStep(underlying) * 2};
            } else {
                if (isVoid(settings.numOfSteps)) {
                    settings.numOfSteps = 5;
                }
                if (isVoid(settings.stepSize)) {
                    settings.stepSize = getStrikeStep(underlying) * 2;
                }
            }

            this.settings = settings;

            this.offsetRows = this.buildOffsetRows(settings.stepSize, settings.numOfSteps);
            this.buildTickerIndex();
            this.startQuoteStream();
            this.subscribeToAllTickers();
            this.calculateAllNetPrices();

            this.isVisible = true;
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Closes the popup
     */
    @DetectMethodChanges()
    public close(): void {
        this.isVisible = false;
        this.reset();
    }

    /**
     * Returns CSS class for net price display
     */
    getNetPriceClass(row: OffsetRow): string {
        if (row.netPrice === null) {
            return 'net-price-na';
        }
        return row.netPrice < 0 ? 'ets-negative-number' : 'ets-positive-number';
    }

    /**
     * Check if row is ATM (offset = 0)
     */
    isAtmRow(row: OffsetRow): boolean {
        return row.offsetPoints === 0;
    }

    onCloseClick(): void {
        this.close();
    }

    // ============ Private Methods ============

    @DetectMethodChanges()
    private reset(): void {
        this._subscriptions.forEach(s => s.unsubscribe());
        this._subscriptions = [];

        const tickers = Object.keys(this.tickerToRow);
        this.lastQuoteCache.unsubscribeTickers(tickers);
        this.quoteSubscriptionActive = false;
        this.destroy$.next();

        this.offsetRows = [];
        this.parsedLegs = [];
        this.tickerToRow = {};
        this.underlyingDisplayName = null;
    }

    private parseLegs(legs: TransactionLeg[]): ParsedLeg[] {
        const parsed: ParsedLeg[] = [];

        for (const leg of legs) {
            try {
                const optionTicker = parseOptionTicker(leg.ticker);
                parsed.push({
                    original: optionTicker,
                    qty: leg.qty,
                    priceAsOwned: leg.priceAsOwned
                });
            } catch (e) {
                console.error(`Failed to parse ticker: ${leg.ticker}`, e);
                return [];
            }
        }

        return parsed;
    }

    getStrikeStep(): number {
        return getStrikeStep(this.underlying);
    }

    private buildOffsetRows(offsetStep: number, numOfSteps: number): OffsetRow[] {
        const rows: OffsetRow[] = [];
        let atmStrike = this.getCurrentAtm();

        for (let i = -numOfSteps; i <= numOfSteps; i++) {
            const offsetPoints = i * offsetStep;
            const row: OffsetRow = {
                offsetPoints: offsetPoints,
                strike: atmStrike - offsetPoints,
                offsetLabel: this.formatOffsetLabel(offsetPoints),
                tickers: this.buildTickersForOffset(offsetPoints),
                netPrice: null
            };
            rows.push(row);
        }

        return rows;
    }

    getCurrentAtm(): number {
        return this._atmStrikeService.getCurrentAtm(this.underlying);
    }

    private formatOffsetLabel(offsetPoints: number): string {
        if (offsetPoints == 0) return "ATM";
        const points = offsetPoints * -1;
        return points > 0 ? `+${points}` : `${points}`;
    }

    private buildTickersForOffset(offsetPoints: number): string[] {

        let atmDistance = 0;

        return this.parsedLegs.map(leg => {
            const newStrike = leg.original.strike + offsetPoints + atmDistance;
            return this.buildOptionTicker(leg.original, newStrike);
        });
    }

    private buildOptionTicker(original: OptionTicker, newStrike: number): string {
        return `${original.expirationTicker} ${original.underlying} ${original.type} ${newStrike} ${original.expiration}`;
    }

    private buildTickerIndex(): void {
        this.tickerToRow = {};

        for (const row of this.offsetRows) {
            for (const ticker of row.tickers) {
                let container = this.tickerToRow[ticker];
                if (isVoid(container)) {
                    container = [];
                    this.tickerToRow[ticker] = container;
                }
                container.push(row);
            }
        }
    }

    private subscribeToAllTickers(): void {
        const tickers = Object.keys(this.tickerToRow);
        this.lastQuoteCache.subscribeTickers(tickers);
    }

    @DetectMethodChanges()
    private calculateAllNetPrices(): void {
        for (const row of this.offsetRows) {
            row.netPrice = this.calculateNetPrice(row);
        }
    }

    private calculateNetPrice(row: OffsetRow): number | null {
        let net = 0;

        for (let i = 0; i < row.tickers.length; i++) {
            const ticker = row.tickers[i];
            const quote = this.lastQuoteCache.getLastQuote(ticker);

            if (isVoid(quote) || !isValidNumber(quote.mid, true)) {
                return null;
            }

            const qty = this.parsedLegs[i].qty;
            const multiplier = this.parsedLegs[i].priceAsOwned ? 1 : -1;
            net += qty * quote.mid * multiplier;
        }

        return net / this.hcf;
    }

    private startQuoteStream(): void {
        if (this.quoteSubscriptionActive) {
            return;
        }

        this.quoteSubscriptionActive = true;

        this._subscriptions.push(
            this.messageBus.of<QuoteDto[]>('QuoteDto')
                .pipe(takeUntil(this.destroy$))
                .subscribe(msg => this.handleQuoteUpdate(msg.payload))
        );

        this._subscriptions.push(
            this.messageBus.of<AtmStrikeChangedEvent>('AtmStrikeChangedEvent')
                .pipe(debounceTime(1000))
                .subscribe(x => this.onAtmStrikeChanged(x.payload))
        )
    }

    @DetectMethodChanges()
    private handleQuoteUpdate(quotes: QuoteDto[]): void {
        if (!quotes || quotes.length === 0) {
            return;
        }

        const affectedRows = Array<OffsetRow>();

        for (const quote of quotes) {
            if (!quote.ticker.startsWith('@')) {
                continue;
            }
            const rows = this.tickerToRow[quote.ticker];
            if (!isVoid(rows)) {
                affectedRows.push(...rows);
            }
        }

        for (const row of affectedRows) {
            row.netPrice = this.calculateNetPrice(row);
        }
    }

    async applySettings() {
        if (!isValidNumber(this.settings.numOfSteps)) {
            this._toastr.error('Number of steps must be a positive integer');
            return;
        }

        if (!isValidNumber(this.settings.stepSize)) {
            this._toastr.error('Step size must be a positive integer');
            return;
        }

        await this._userSettings
            .setValue(`pricing-steps.${this.underlying}.settings`, this.settings);

        this.reset();

        setTimeout(() => {
            this.show(this.originalLegs, this.hcf, this.metadata);
        });
    }

    private onAtmStrikeChanged(payload: AtmStrikeChangedEvent) {
        setTimeout(() => {
            this.show(this.originalLegs, this.hcf, this.metadata);
        });
    }

    protected onPriceDblClick(row: OffsetRow) {
        console.log(row);
    }
}